<template>
  <div class="insights-navigation-lists">
    <AppAccordion v-model:active="active">
      <AppAccordionItem>
        <template #accordion-trigger>
          {{ teamsTitle }}
        </template>
        <template #accordion-content>
          <InsightsNavigationEngagement />
          <InsightsNavigationList
            :items="teamList"
            type="team"
            :team-id="teamId"
          />
        </template>
      </AppAccordionItem>

      <AppAccordionItem test-id="insights-navigation-members-trigger">
        <template #accordion-trigger>
          {{ usersTitle }}
        </template>

        <template #accordion-content>
          <InsightsNavigationEngagement />
          <InsightsNavigationLazyList
            :team-id="teamId"
            type="user"
          />
        </template>
      </AppAccordionItem>
    </AppAccordion>
  </div>
</template>

<script>
import InsightsNavigationList from '~/components/insights/InsightsNavigationList.vue';
import InsightsNavigationLazyList from '~/components/insights/InsightsNavigationLazyList.vue';
import InsightsNavigationEngagement from '~/components/insights/InsightsNavigationEngagement.vue';

import { mapGetters, mapState } from 'vuex';
import { cloneDeep } from 'lodash-es';
import { pluralize } from '~/utils/string_utils';
import AppAccordion from '~/components/AppAccordion.vue';
import AppAccordionItem from '~/components/AppAccordionItem.vue';

export default {
  name: 'InsightsNavigationLists',
  components: {
    InsightsNavigationList,
    InsightsNavigationLazyList,
    InsightsNavigationEngagement,
    AppAccordion,
    AppAccordionItem,
  },
  data: () => ({
    initialActive: null,
  }),
  computed: {
    ...mapGetters([
      'selectedFilter',
      'users',
      'usersTotal',
      'teams',
      'teamsTotal',
      'isAllMembersDashboard',
      'isUserDashboard',
      'isIndividualTeamDashboard',
      'previousDashboardId',
      'currentUserBelongsToPrevTeam',
      'previousDashboardTeamName',
      'previousDashboardIsIndividualTeam',
      'loadingUsers',
    ]),
    active: {
      get() {
        return this.initialActive;
      },
      // AppAccordion always emits null right back after consuming the value (see its `active`
      // watcher), and the default is only ever sent once (see created()), so there's nothing to
      // store here - and since it never changes, that watcher only fires the one time anyway.
      set() {},
    },
    ...mapState(['filters', 'previousView']),
    isReady() {
      return !this.loadingUsers?.initial ?? false;
    },
    maybeUserId() {
      return (this.filters.user_id && parseInt(this.filters.user_id)) || null; // only numbers
    },
    isUserAndBelongsToPreviousTeam() {
      return this.isUserDashboard && this.previousDashboardIsIndividualTeam && this.currentUserBelongsToPrevTeam;
    },
    teamId() {
      return this.previousView?.includes('team_id')
        ? this.previousView && String(parseInt(this.previousView))
        : this.filters.team_id;
    },
    usersTitle() {
      if (!this.isReady) {
        return 'Members';
      }

      return `${this.teamName || this.previousDashboardTeamName || ''} ${pluralize(this.usersLength, 'Member')}`;
    },
    usersLength() {
      return this.userList?.length;
    },
    teamsTitle() {
      if (!this.isReady) {
        return 'Teams';
      }

      if (this.maybeUserId) {
        return `${this.selectedUser?.name || ''} ${this.filteredTeams.length || 'no'} ${pluralize(
          this.filteredTeams.length,
          'Team',
        )}`;
      }

      return `${this.teamsTotal} ${pluralize(this.teamsTotal.length, 'Team')}`;
    },
    selectedUser() {
      return this.filters.user_id && this.users.find((user) => user.id === parseInt(this.filters.user_id));
    },
    userList() {
      let _usersList = cloneDeep(this.users);

      // Put the default(the selected one) user on top of the list
      if (this.filters.user_id) {
        const defaultUser = this.users.find((user) => user.id === parseInt(this.filters.user_id));
        const defaultUserIndex = _usersList.findIndex((user) => user.id === parseInt(this.filters.user_id));

        if (defaultUser && defaultUserIndex !== -1) {
          _usersList = [
            defaultUser,
            ..._usersList.slice(0, defaultUserIndex),
            ..._usersList.slice(defaultUserIndex + 1),
          ];
        }
      }

      if (this.isUserAndBelongsToPreviousTeam) {
        return _usersList.filter((user) => user.team_ids?.includes(parseInt(this.previousDashboardId)));
      }

      if (this.isIndividualTeamDashboard) {
        const teamId = parseInt(this.filters.team_id);
        const filteredByTeam = _usersList.filter((user) => user.team_ids?.includes(teamId));

        return filteredByTeam;
      }

      return _usersList;
    },
    showTeamEngagementHeader() {
      return !!this.teamList.length;
    },
    showMemberEngagementHeader() {
      return !!this.userList.length;
    },
    teamList() {
      return this.filteredTeams.map((team) => ({
        ...team,
        teamMembers: this.users.filter((user) => user.team_ids?.includes(team.id)),
      }));
    },
    filteredTeams() {
      if (!this.maybeUserId) {
        return this.teams;
      }

      return this.teams.filter((team) => team.team_member_ids.includes(this.maybeUserId));
    },
    teamName() {
      return this.teams.find((team) => team.id === Number(this.teamId))?.name;
    },
  },
  created() {
    this.initialActive = this.maybeUserId || this.teamId ? 1 : 0;
  },
};
</script>
